import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Spin, Tag, Typography, message } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_BASE_URL, getAuthHeaders, parseApiJson } from '../api';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

export interface ResultsLinkRequestStatus {
  tag_request_status: string;
  wants_result_tagging: boolean;
  tag_request_note: string | null;
  can_submit: boolean;
}

const STATUS_LABELS: Record<string, { color: string; label: string }> = {
  none: { color: 'default', label: 'Not requested' },
  pending_admin_review: { color: 'gold', label: 'Pending TrackiAI review' },
  approved: { color: 'green', label: 'Linked' },
  rejected: { color: 'red', label: 'Not linked — you may submit again' },
};

/**
 * Lets signed-in users ask the TrackiAI team to link their account to existing results.
 */
const ProfileLinkResultsCard: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<ResultsLinkRequestStatus | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/results-link-request`, { headers });
      const payload = await parseApiJson<ResultsLinkRequestStatus & { error?: string }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to load link request status');
      setStatus(payload);
      setApiError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load status';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const onSubmit = async (values: { tag_request_note: string }) => {
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/results-link-request`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_request_note: values.tag_request_note }),
      });
      const payload = await parseApiJson<{ error?: string; message?: string }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to submit request');
      message.success(payload.message || 'Request sent to the TrackiAI team');
      form.resetFields();
      loadStatus();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusKey = status?.tag_request_status || 'none';
  const statusMeta = STATUS_LABELS[statusKey] || STATUS_LABELS.none;
  const showForm = status?.can_submit === true;

  return (
    <Card style={{ marginBottom: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          <LinkOutlined /> Link my competition results
        </Title>
        <Button icon={<ReloadOutlined />} size="small" onClick={loadStatus} loading={loading} aria-label="Refresh status">
          Refresh
        </Button>
      </Space>
      <Paragraph type="secondary">
        If you already have results in our database under your name, ask the TrackiAI team to connect them to this
        account. Include your name as it appears on results, birth date, club, and events or years to search.
      </Paragraph>

      {apiError && <Alert type="error" showIcon message={apiError} style={{ marginBottom: 16 }} />}

      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Status: </Text>
            <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          </div>
          {status?.tag_request_note && statusKey === 'pending_admin_review' && (
            <Alert
              type="info"
              showIcon
              message="Your request"
              description={status.tag_request_note}
            />
          )}
          {showForm && (
            <Form form={form} layout="vertical" onFinish={onSubmit}>
              <Form.Item
                name="tag_request_note"
                label="Details for the TrackiAI team"
                rules={[
                  { required: true, message: 'Please describe how we can find your results' },
                  { min: 10, message: 'Please provide at least 10 characters' },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="e.g. Full name on results, DOB, club/school, events (100m, long jump), competitions or years"
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Send link request to TrackiAI
              </Button>
            </Form>
          )}
          {statusKey === 'pending_admin_review' && (
            <Text type="secondary">We will review your request and link matching results when found.</Text>
          )}
          {statusKey === 'approved' && (
            <Text type="secondary">Your account is linked. Check Athlete Results or ask TrackiAI for your marks.</Text>
          )}
        </Space>
      </Spin>
    </Card>
  );
};

export default ProfileLinkResultsCard;
